import React, { useCallback, useMemo, useState } from 'react';
import { useIntl } from 'umi';
import { LANGUAGE_EN } from '@home/_config/config';
import { getLocalText } from '@/pages/datalab/utils/utils';
import SelectWithButton from '@/pages/datalab/components/SelectWithButton';
import LabelText from '@/pages/datalab/components/LabelText';
import SelectorWithConfirm from '@/pages/datalab/components/SelectorWithConfirm';
import LastInputAndSelector from '@/pages/datalab/components/StringSelectorWithTag/FieldTimeRange/LastInputAndSelector';
import type { TSelectorOption } from '@/pages/datalab/components/SelectorWithConfirm';
import './index.scss';

/**
 * 默认时间范围常量
 */
const DEFAULT_LAST_COUNT = '2';
const DEFAULT_DATE_TYPE = 'day';

/**
 * 时间范围选项值的类型定义
 */
type TTimeRangeValue = {
  lastCount: string;
  dateType: string;
  isCustomOption: boolean;
  last_day: string;
};

/**
 * 时间字段信息接口
 */
interface ITimeField {
  display_name: string;
  display_name_en: string;
  column: string;
  column_type: string;
  operation: string;
  table_id?: string;
  table_code?: string;
  sql_expression?: string;
  chart_id?: string;
}

/**
 * 日期列字段接口
 */
interface IDateColumn {
  display_name?: string;
  display_name_en?: string;
  column?: string;
  name: string;
  type: string;
  chart_name_cn?: string;
  chart_name_en?: string;
  chart_id?: string;
  sql_expression?: string;
}

/**
 * 时间字段选项接口
 */
interface ITimeFieldOption {
  text: string;
  value: string;
  columnInfo: ITimeField;
}

/**
 * FieldTimeRange 组件 Props 接口
 */
interface IFieldTimeRangeProps {
  value?: TTimeRangeValue & ITimeField;
  onChange?: (value: TTimeRangeValue & ITimeField) => void;
  dateColumns?: IDateColumn[];
}

/**
 * 国际化格式化函数类型
 */
type TFormatMessage = (descriptor: { id: string; }, values?: Record<string, any>) => string;

/**
 * 判断文本选项是否被选中
 * 通过比较选项值和内部状态的值来判断，仅当不是自定义选项且值完全匹配时返回 true
 */
const isTextOptionSelected = (
  option: TSelectorOption<TTimeRangeValue>,
  innerState: TTimeRangeValue | undefined
): boolean => {
  if (!innerState || option.type !== 'text') {
    return false;
  }
  
  const { lastCount, dateType } = option.value;

  // 对于「全部时间」(all_time)，只要 lastCount 匹配即可视为选中，忽略 dateType 等其他字段，
  // 以兼容历史数据中 dateType 可能被写成 'day' 的情况
  if (lastCount === 'all_time') {
    return innerState.lastCount === 'all_time';
  }
  
  return (
    !innerState.isCustomOption &&
    innerState.dateType === dateType &&
    innerState.lastCount === lastCount
  );
};

/**
 * 生成时间范围选择器的选项配置
 * @param formatMessage - 国际化格式化函数
 * @returns 返回选项数组
 */
const getTimeRangeOptions = (
  formatMessage: TFormatMessage
): TSelectorOption<TTimeRangeValue>[] => {
  return [
  
    {
      type: 'text',
      text: formatMessage({ id: 'dbl_time_all' }),
      value: { lastCount: 'all_time', dateType: '', isCustomOption: false, last_day: '-1' },
      selected: isTextOptionSelected,
    },
    {
      type: 'text',
      text: formatMessage({ id: 'dbl_date_day_last_1' }),
      value: { lastCount: '1', dateType: 'day', isCustomOption: false, last_day: '1' },
      selected: isTextOptionSelected,
    },
    {
      type: 'text',
      text: formatMessage({ id: 'dbl_date_day_last_prefix' }, { last: 7 }),
      value: { lastCount: '7', dateType: 'day', isCustomOption: false, last_day: '-1' },
      selected: isTextOptionSelected,
    },
    {
      type: 'text',
      text: formatMessage({ id: 'dbl_date_day_last_prefix' }, { last: 30 }),
      value: { lastCount: '30', dateType: 'day', isCustomOption: false, last_day: '-1' },
      selected: isTextOptionSelected,
    },
    {
      type: 'text',
      text: formatMessage({ id: 'dbl_date_day_last_prefix' }, { last: 180 }),
      value: { lastCount: '180', dateType: 'day', isCustomOption: false, last_day: '-1' },
      selected: isTextOptionSelected,
    },
    {
      type: 'component',
      render: (
        innerValue: TTimeRangeValue,
        setState: (value: TTimeRangeValue) => void
      ) => {
        const { lastCount, dateType, isCustomOption } = innerValue;
        return (
          <LastInputAndSelector
            isSelected={isCustomOption}
            lastCount={isCustomOption ? lastCount : DEFAULT_LAST_COUNT}
            granularity={(isCustomOption ? dateType : DEFAULT_DATE_TYPE) as 'day' | 'week' | 'month'}
            onChange={(value: Partial<TTimeRangeValue>) => {
              setState({ ...innerValue, ...value });
            }}
          />
        );
      },
      selected: (
        option: TSelectorOption<TTimeRangeValue>,
        innerState: TTimeRangeValue | undefined
      ): boolean => {
        return innerState?.isCustomOption ?? false;
      },
    },
  ];
};

const FieldTimeRange: React.FC<IFieldTimeRangeProps> = (props) => {
  const { value, onChange, dateColumns = [] } = props;
  const { formatMessage, locale } = useIntl();
  const isEn = locale === LANGUAGE_EN;
  const [timeField, setTimeField] = useState<ITimeField>({
    display_name: value?.display_name ?? '',
    display_name_en: value?.display_name_en ?? '',
    column: value?.column ?? '',
    column_type: value?.column_type ?? '',
    operation: 'recent',
    table_id: value?.table_id,
    table_code: value?.table_code,
    sql_expression: value?.sql_expression,
    chart_id: value?.chart_id,
  });
  const timeFieldOptions = useMemo((): ITimeFieldOption[] => {
    return dateColumns.map((field: IDateColumn): ITimeFieldOption => {
      let text: string = field.display_name || field.column || field.name;
      if (field?.chart_name_cn || field?.chart_name_en) {
        const chartName = getLocalText(isEn, field?.chart_name_cn, field?.chart_name_en);
        text = `${text}(${chartName})`;
      }
      
      return {
        text,
        value: `${field.name}${field?.chart_id ?? ''}`,
        columnInfo: {
          display_name: field.display_name ?? '',
          display_name_en: field.display_name_en ?? '',
          column: field.name,
          column_type: field.type,
          operation: 'recent',
          sql_expression: field.sql_expression,
          chart_id: field.chart_id,
          table_id: field?.table_id,
          table_code: field?.table_code,
        },
      };
    });
  }, [dateColumns, isEn]);
  
  /**
 * 处理时间字段选择变化
 * SelectWithButton 的 onChange 回调参数类型来自 tea-component，需要兼容其类型定义
 * @param value - 选中的值
 * @param context - SelectWithButton 的 onChange 回调上下文（tea-component 定义的类型）
 */
  const handleTimeFieldChange = (value: string, context: { option?: unknown; }): void => {
  // 类型守卫：检查 context.option 是否包含 columnInfo
    if (context?.option?.columnInfo) {
      const info = context.option.columnInfo;
      setTimeField({
        ...info,
        table_id: info?.table_id || timeField?.table_id,
        table_code: info?.table_code || timeField?.table_code,
      });
    }
  };
  
  const options = useMemo((): TSelectorOption<TTimeRangeValue>[] => {
    return getTimeRangeOptions(formatMessage);
  }, [formatMessage]);
  
  const handleDateChange = (value: TTimeRangeValue): void => {
    onChange?.({
      ...value,
      ...timeField,
    });
  };
  
  /**
   * 缓存传递给 SelectorWithConfirm 的 value 对象
   * 避免因为对象引用变化导致内部状态被重置
   */
  const selectorValue = useMemo((): TTimeRangeValue => {
    return {
      lastCount: value?.lastCount ?? '',
      dateType: value?.dateType ?? '',
      isCustomOption: value?.isCustomOption ?? false,
      last_day: value?.last_day ?? '-1',
    };
  }, [value?.lastCount, value?.dateType, value?.isCustomOption]);
  
  /**
   * 生成按钮显示文本
   * 根据不同的时间范围值生成对应的显示文本
   */
  const handleButtonText = useCallback((
    controlledValue?: TTimeRangeValue,
    selectedOption?: TSelectorOption<TTimeRangeValue>
  ): React.ReactNode => {
    if (!controlledValue) {
      return '';
    }
    
    const { lastCount, dateType } = controlledValue;
    
    // 处理大于1的时间范围（如最近6个月）
    if (Number(lastCount) > 1) {
      const dateTypeText = formatMessage({ id: `dbl_date_${dateType}s` });
      return formatMessage(
        { id: 'dbl_time_last_datetype' },
        { lastCount, dateType: dateTypeText }
      );
    }
    
    // 处理全部时间选项
    if (lastCount === 'all_time') {
      return selectedOption && 'text' in selectedOption ? selectedOption.text : '';
    }
    
    // 处理单个时间单位（最近1天/周/月）
    return formatMessage({ id: `dbl_date_${dateType}_last_1` });
  }, [formatMessage]);

  return (
    <div className="dbl-field-time-range">
      <LabelText preset="838697-14-400">
        {formatMessage({ id: 'dbl_time_string_filter_label1' })}
      </LabelText>
      <SelectorWithConfirm
        className="dbl-field-time-selector"
        overlayClassName="dbl-field-time-selector-overlay"
        options={options}
        onChange={handleDateChange}
        value={selectorValue}
        headerComponent={
          <SelectWithButton
            prefix={formatMessage({ id: 'dbl_time_string_filter_label2' })}
            matchButtonWidth
            options={timeFieldOptions}
            value={`${timeField.column}${timeField?.chart_id ?? ''}`}
            onChange={handleTimeFieldChange}
          />
        }
        button={handleButtonText}
      />
    </div>
  );
  
};

export default FieldTimeRange;
